/**
 * SilentFilmPage ✦ — iter201
 * ─────────────────────────────────────────────────────────────
 * Vertical silent-film recap surface.
 *
 * The brief locks: black & white · grain · soft flicker · silent-
 * film pacing · 20–45s · MP4 · no AI-cringe, no TikTok overlays,
 * no zoom spam, no loud captions. The page is glass; the actual
 * cinema lives inside the encoded MP4 served by
 * /api/events/{event_id}/silent-film/mp4.
 *
 * Operator-only. Single "Regenerate ✦" gesture. No configurator.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INK_LIGHT = 'rgba(245,239,222,0.92)';
const INK_SOFT = 'rgba(245,239,222,0.62)';
const INK_FAINT = 'rgba(245,239,222,0.42)';
const GOLD = '#c19a4b';
const FONT_DISPLAY = "'Cormorant Garamond','Playfair Display',Georgia,serif";
const FONT_BODY = "'Inter',system-ui,sans-serif";

const SilentFilmPage = () => {
  const { slug } = useParams();
  const [eventId, setEventId] = useState(null);
  const [eventName, setEventName] = useState(null);
  const [mp4Url, setMp4Url] = useState(null);
  const [status, setStatus] = useState('resolving'); // resolving | composing | ready | gathering | error
  const [errorLine, setErrorLine] = useState('');

  useEffect(() => {
    if (!slug) return undefined;
    let aborted = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        if (aborted) return;
        setEventId(r.data?.event_id);
        setEventName(r.data?.event_name || null);
        setStatus('idle');
      } catch {
        if (!aborted) {
          setStatus('error');
          setErrorLine('No evening found here ✦');
        }
      }
    })();
    return () => { aborted = true; };
  }, [slug]);

  const render = useCallback(async () => {
    if (!eventId) return;
    setStatus('composing');
    setErrorLine('');
    try {
      // iter202 — same persistence contract as the Chronicle. First
      // visit serves the durable cache; `Regenerate ✦` forces a
      // fresh encode.
      const force = status === 'ready' || status === 'gathering';
      const r = await axios.post(
        `${API}/events/${eventId}/silent-film/render`,
        null,
        {
          // Encoding can take 30-60s; axios default is too aggressive.
          timeout: 180000,
          params: force ? { regenerate: true } : {},
        },
      );
      if (r.data?.status === 'settled' && r.data?.mp4_url) {
        setMp4Url(`${BACKEND_URL}${r.data.mp4_url}?t=${Date.now()}`);
        setStatus('ready');
      } else {
        setStatus('gathering');
      }
    } catch (e) {
      setStatus('error');
      setErrorLine(e?.response?.data?.detail || 'The film is gathering itself ✦');
    }
  }, [eventId, status]);

  // Auto-render once on first arrival; afterwards only on explicit
  // operator gesture. The brief: at most "Regenerate ✦".
  useEffect(() => {
    if (status === 'idle' && eventId) {
      render();
    }
  }, [status, eventId, render]);

  const statusLine = useMemo(() => {
    if (status === 'resolving') return 'Finding the evening ✦';
    if (status === 'composing') return 'The film is gathering itself ✦';
    if (status === 'gathering') return 'The film is still gathering itself ✦';
    if (status === 'error') return errorLine || 'The film is gathering itself ✦';
    if (status === 'ready') return 'The film has settled ✦';
    return '';
  }, [status, errorLine]);

  return (
    <div
      data-testid="silent-film-page"
      data-status={status}
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at top, #1a1611 0%, #0a0805 80%)',
        color: INK_LIGHT, fontFamily: FONT_BODY,
        padding: '24px 22px 80px',
      }}
    >
      <BackButton
        fallbackHref={`/event/${slug}`}
        label="Back"
        variant="subtle"
        testId="silent-film-back-btn"
      />

      <header style={{ maxWidth: 720, margin: '40px auto 0' }}>
        <p
          data-testid="silent-film-eyebrow"
          style={{
            margin: 0, fontFamily: FONT_BODY, fontSize: 11,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: INK_FAINT,
          }}
        >
          PRESERVE THE NIGHT ✦  ·  OPERATOR
        </p>
        <h1
          data-testid="silent-film-title"
          style={{
            margin: '14px 0 0', fontFamily: FONT_DISPLAY, fontWeight: 500,
            fontSize: 'clamp(38px,5.4vw,56px)', lineHeight: 1.05,
            letterSpacing: '-0.015em', color: INK_LIGHT,
          }}
        >
          The Silent Film ✦
        </h1>
        {eventName && (
          <p style={{
            margin: '10px 0 0', fontFamily: FONT_DISPLAY, fontStyle: 'italic',
            fontSize: 16, color: INK_SOFT,
          }}>{eventName}</p>
        )}
        <p
          data-testid="silent-film-status"
          style={{
            margin: '22px 0 0', fontFamily: FONT_DISPLAY, fontStyle: 'italic',
            fontSize: 'clamp(17px,2vw,20px)', color: GOLD,
          }}
        >
          {statusLine}
        </p>
      </header>

      <main
        data-testid="silent-film-stage"
        style={{
          maxWidth: 540, margin: '40px auto 0',
        }}
      >
        {/* 9:16 stage — the encoded MP4 is vertical so the stage
            reserves the right aspect even before the file lands. */}
        <div
          style={{
            position: 'relative',
            width: '100%', aspectRatio: '9 / 16',
            background: '#0a0805',
            border: `1px solid rgba(193,154,75,0.22)`,
            boxShadow: '0 18px 60px rgba(0,0,0,0.55)',
            overflow: 'hidden',
          }}
        >
          {mp4Url ? (
            <video
              data-testid="silent-film-video"
              src={mp4Url}
              controls
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center', padding: 24,
              textAlign: 'center',
            }}>
              <p style={{
                margin: 0, fontFamily: FONT_DISPLAY, fontStyle: 'italic',
                fontSize: 18, color: INK_SOFT, lineHeight: 1.45,
              }}>
                {statusLine}
              </p>
            </div>
          )}
        </div>

        <div style={{
          marginTop: 24, display: 'flex', gap: 12, flexWrap: 'wrap',
          justifyContent: 'center',
        }}>
          <button
            type="button"
            data-testid="silent-film-regenerate-btn"
            onClick={render}
            disabled={status === 'composing' || !eventId}
            style={{
              padding: '12px 22px', borderRadius: 4,
              border: `1px solid ${GOLD}`,
              background: status === 'composing' ? 'rgba(193,154,75,0.10)' : 'transparent',
              color: INK_LIGHT, fontFamily: FONT_DISPLAY, fontStyle: 'italic',
              fontSize: 15, letterSpacing: '0.02em',
              cursor: status === 'composing' || !eventId ? 'default' : 'pointer',
            }}
          >
            Regenerate ✦
          </button>
        </div>
      </main>
    </div>
  );
};

export default SilentFilmPage;
